/**
 * RoyaleShareSheet
 *
 * Shows the host (and any joined player) the lobby's join code and a
 * shareable link, each with copy-to-clipboard. Anyone who opens the link
 * lands on PuzzleRoyaleLobby with the code prefilled (see Application.tsx
 * linking + PuzzleRoyaleLobby route param parsing).
 *
 * Web-only clipboard via navigator.clipboard (matches the repo's existing
 * pattern, e.g. RenewalBanner). Falls back to just showing the value.
 */

import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';

const TEAL_DARK = '#1F6C63';
const TEXT_DARK = '#262322';
const TEXT_MUTED = '#8A7E5F';
const BORDER = 'rgba(38, 35, 34, 0.10)';

interface Props {
  shareCode: string;
  /** Origin override for tests; defaults to window.location.origin. */
  origin?: string;
}

function buildLink(shareCode: string, origin?: string): string {
  const base =
    origin ??
    (typeof window !== 'undefined' && window.location
      ? window.location.origin
      : '');
  // The royale lobby lives in the Fun Challenges lobby's Multiplayer tab — not
  // its own route. Deep-link there with the tab + join code as params.
  return `${base}/FunChallengesLobby?mode=multiplayer&join=${encodeURIComponent(
    shareCode,
  )}`;
}

function copy(text: string): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // swallow — UI falls back to "copy unavailable"
  }
  return false;
}

export default function RoyaleShareSheet({shareCode, origin}: Props) {
  const link = buildLink(shareCode, origin);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  const handleCopy = (which: 'code' | 'link', text: string) => {
    if (copy(text)) {
      setCopied(which);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  return (
    <View style={styles.root}>
      <Row
        label="Join code"
        value={shareCode}
        copied={copied === 'code'}
        onCopy={() => handleCopy('code', shareCode)}
      />
      <Row
        label="Invite link"
        value={link}
        valueNumberOfLines={1}
        copied={copied === 'link'}
        onCopy={() => handleCopy('link', link)}
      />
    </View>
  );
}

function Row({
  label,
  value,
  copied,
  onCopy,
  valueNumberOfLines,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  valueNumberOfLines?: number;
}) {
  return (
    <View style={styles.row}>
      <View style={styles.rowBody}>
        <CCText style={styles.rowLabel}>{label}</CCText>
        <CCText style={styles.rowValue} numberOfLines={valueNumberOfLines}>
          {value}
        </CCText>
      </View>
      <Pressable
        onPress={onCopy}
        style={({pressed}) => [
          styles.copyBtn,
          pressed && styles.copyBtnPressed,
        ]}>
        <CCText style={styles.copyBtnText}>{copied ? 'Copied' : 'Copy'}</CCText>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {gap: vs(10)},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.White,
  },
  rowBody: {flex: 1, minWidth: 0},
  rowLabel: {
    fontSize: FontSizes[11] || 11,
    fontWeight: '700',
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 2,
  },
  rowValue: {
    fontSize: FontSizes[14] || 14,
    fontWeight: '700',
    color: TEXT_DARK,
  },
  copyBtn: {
    paddingHorizontal: 16,
    paddingVertical: 9,
    borderRadius: 10,
    backgroundColor: 'rgba(61, 171, 158, 0.12)',
    borderWidth: 1,
    borderColor: 'rgba(61, 171, 158, 0.30)',
  },
  copyBtnPressed: {backgroundColor: 'rgba(61, 171, 158, 0.22)'},
  copyBtnText: {
    fontSize: FontSizes[13] || 13,
    fontWeight: '700',
    color: TEAL_DARK,
  },
});

export {buildLink};
